/**
 * Theme Toggle Component
 *
 * Cycles light → dark → system on each click.
 * Persists choice in localStorage with smooth transitions.
 */

import { useState, useEffect } from "react";
import { Moon, Sun, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";

type Theme = "light" | "dark" | "system";

const THEME_KEY = "wicloud_theme";

function nextTheme(current: Theme): Theme {
  if (current === "light") return "dark";
  if (current === "dark") return "system";
  return "light";
}

export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem(THEME_KEY);
    // Light is the product default — a first-time visitor with no saved
    // preference should not inherit the OS/browser's dark-mode setting.
    return (stored as Theme) || "light";
  });

  useEffect(() => {
    const root = window.document.documentElement;

    const apply = () => {
      root.classList.remove("light", "dark");

      let effectiveTheme: "light" | "dark";

      if (theme === "system") {
        const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
          .matches
          ? "dark"
          : "light";
        effectiveTheme = systemTheme;
      } else {
        effectiveTheme = theme;
      }

      root.classList.add(effectiveTheme);
    };

    apply();
    localStorage.setItem(THEME_KEY, theme);

    if (theme === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    }
  }, [theme]);

  const cycleTheme = () => {
    setTheme((t) => nextTheme(t));
  };

  const label =
    theme === "light"
      ? "Color theme: light. Click to switch to dark."
      : theme === "dark"
        ? "Color theme: dark. Click to switch to system."
        : "Color theme: system. Click to switch to light.";

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      title={label}
      onClick={cycleTheme}
    >
      {theme === "light" ? (
        <Sun className="h-5 w-5 transition-opacity" />
      ) : theme === "dark" ? (
        <Moon className="h-5 w-5 transition-opacity" />
      ) : (
        <Monitor className="h-5 w-5 transition-opacity" />
      )}
    </Button>
  );
}
